import React, { useState } from 'react';
import Datetime from 'react-datetime';
import { Global } from '@emotion/core';
import { css } from 'emotion';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  Path,
} from 'react-hook-form';
import {
  Type,
  ButtonSecondary,
  IconChevronDown,
  ButtonPrimary,
} from '@spotify-internal/encore-web';
import { Modal } from '@spotify-internal/anchor-ds';
import { useT } from 'client/i18n/hooks/useT';
import { Container, DatetimeGlobalStyles } from './styles';
import {
  getDisplayValue,
  roundToNextHour,
  getCurrentDateAtUpcomingHour,
} from './utils';

export const ControlledFieldDatePicker = <T extends FieldValues>({
  label,
  name = 'publishOn' as Path<T>,
  className,
  control,
}: {
  label: string;
  name: FieldPath<T>;
  className?: any;
  control?: Control<T>;
}) => {
  const t = useT();
  const [isShowingSchedulerModal, setIsShowingSchedulerModal] = useState(false);
  const {
    field: { value, onChange },
  } = useController<T, FieldPath<T>>({ control, name });
  const [valueOnOpen, setValueOnOpen] = useState(value);

  const dateTimeValue =
    !value || value === '' ? getCurrentDateAtUpcomingHour() : value;

  return (
    <Container className={className}>
      <Type
        as="span"
        weight="bold"
        aria-label={name}
        className={css`
          margin-bottom: 8px;
        `}
      >
        {label}
      </Type>
      <ButtonSecondary
        onClick={() => {
          setIsShowingSchedulerModal(true);
          setValueOnOpen(value);
        }}
        iconTrailing={IconChevronDown}
        className={css`
          border-radius: 4px;
          background: var(--background-base);
          ::after {
            border-radius: 4px;
          }
        `}
        type="button" // Don't want button to submit form
      >
        {getDisplayValue(
          value,
          null,
          t('FieldDatePicker.NowLabel', 'Now', 'Episode publish now label')
        )}
      </ButtonSecondary>
      <Modal
        aria-labelledby="schedule-publish-title"
        isShowing={isShowingSchedulerModal}
        isShowingCloseButton={true}
        onClose={() => {
          onChange(valueOnOpen);
          setIsShowingSchedulerModal(false);
        }}
      >
        <Modal.Title id="schedule-publish-title">
          {getDisplayValue(
            value,
            t(
              'FieldDatePicker.ScheduleForLabel',
              'Schedule for',
              'Epsiode schedule for label'
            ),
            t(
              'FieldDatePicker.PublishNowLabel',
              'Publish now',
              'Episode publish now label'
            )
          )}
        </Modal.Title>
        <Modal.Body
          as="div"
          className={css`
            width: 100%;
          `}
        >
          <Global styles={DatetimeGlobalStyles} />
          <Datetime
            className="DatetimeContainer"
            input={false}
            timeConstraints={{
              minutes: { min: 0, max: 0, step: 60 },
              seconds: { min: 0, max: 0, step: 60 },
              milliseconds: { min: 0, max: 0, step: 1000 },
            }}
            onChange={date => {
              onChange(roundToNextHour(date));
            }}
            value={dateTimeValue}
          />
          {t(
            'FieldDatePicker.LocalTimeZonesMessage',
            'All times are local',
            'Date picker local time zones message'
          )}
        </Modal.Body>
        <Modal.Footer>
          <ButtonSecondary
            onClick={() => {
              onChange(null);
              setIsShowingSchedulerModal(false);
            }}
          >
            {t(
              'FieldDatePicker.ResetToNowButton',
              'Reset to now',
              'Date picker reset to now button text'
            )}
          </ButtonSecondary>
          <ButtonPrimary onClick={() => setIsShowingSchedulerModal(false)}>
            {t('Button.Confirm', 'Confirm', 'Confirm button text')}
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
